import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { ApiClient } from '../utils/apiClient.js';
import Sidebar from './Sidebar.jsx';
import NotificationBell from './NotificationBell.jsx';

/**
 * DashboardLayout component providing left sidebar navigation and layout for user dashboard pages
 */
const DashboardLayout = ({ children }) => {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { theme, updateTheme, loading: themeLoading } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const apiClient = useRef(new ApiClient(getToken)).current;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('dashboard-sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [userFirstName, setUserFirstName] = useState('');
  const [userDataLoaded, setUserDataLoaded] = useState(false);

  // Navigation items for regular users
  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: '📊',
      description: 'Overview and statistics'
    },
    {
      name: 'Notifications',
      href: '/notifications',
      icon: <NotificationBell collapsed={sidebarCollapsed} />,
      description: 'View important updates and announcements'
    },
    {
      name: 'Conversations',
      href: '/conversations',
      icon: '💬',
      description: 'Manage and review conversations'
    },
    {
      name: 'Parrots',
      href: '/parrots',
      icon: '🦜',
      description: 'Manage your AI parrots'
    },
    {
      name: 'Phone Numbers',
      href: '/phone-numbers',
      icon: '📞',
      description: 'Purchase and manage phone numbers'
    },
    {
      name: 'Knowledge Base',
      href: '/knowledge-base',
      icon: '📚',
      description: 'Upload and manage knowledge files'
    },
    {
      name: 'Recordings',
      href: '/recordings',
      icon: '🎙️',
      description: 'View call recordings'
    },
    {
      name: 'Call Forwarding',
      href: '/call-forwarding',
      icon: '📲',
      description: 'Configure call forwarding'
    },
    {
      name: 'Billing',
      href: '/billing',
      icon: '💳',
      description: 'Manage subscription and billing'
    },
    {
      name: 'Profile',
      href: '/profile',
      icon: '👤',
      description: 'Account settings and profile'
    }
  ];

  // Persist sidebar collapsed state and sync with backend
  const handleToggleCollapse = async (collapsed) => {
    setSidebarCollapsed(collapsed);
    localStorage.setItem('dashboard-sidebar-collapsed', JSON.stringify(collapsed));
    
    // Save navigation preference to backend
    if (isSignedIn) {
      try {
        await apiClient.put('/api/user/preferences', { navigation_collapsed: collapsed });
      } catch (error) {
        console.warn('Failed to save navigation preference to backend:', error);
      }
    }
  };

  // Load user navigation preference and user data from backend
  useEffect(() => {
    const loadUserData = async () => {
      if (!isSignedIn || !isLoaded) return;
      
      try {
        // Load user preferences
        const preferencesData = await apiClient.get('/api/user/preferences');
        if (preferencesData.success && preferencesData.preferences.navigation_collapsed !== undefined) {
          setSidebarCollapsed(preferencesData.preferences.navigation_collapsed);
          localStorage.setItem('dashboard-sidebar-collapsed', JSON.stringify(preferencesData.preferences.navigation_collapsed));
        }

        // Load user data for greeting
        const userData = await apiClient.get('/api/auth/me');
        setUserFirstName(userData.user?.first_name || '');
        
        // Mark user data as loaded regardless of success/failure
        setUserDataLoaded(true);
      } catch (error) {
        console.warn('Failed to load user data:', error);
        // Still mark as loaded even on error so we don't show loading forever
        setUserDataLoaded(true);
      }
    };
    
    loadUserData();
  }, [isSignedIn, isLoaded, getToken]);

  // Generate time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    const firstName = userFirstName || 'there';
    
    if (hour >= 0 && hour < 12) {
      return `Good morning ${firstName}`;
    } else if (hour >= 12 && hour < 18) {
      return `Good afternoon ${firstName}`;
    } else {
      return `Good evening ${firstName}`;
    }
  };

  return (
    <div className={`min-h-screen flex ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'} ${themeLoading ? 'theme-loading' : ''}`} data-theme={theme}>
      <Sidebar
        items={navigationItems}
        collapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleCollapse}
        currentPath={location.pathname}
      />
      
      {/* Main content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        {/* Dynamic greeting at top level */}
        <div className="px-6 pt-6 pb-2">
          {userDataLoaded ? (
            <h1 className={`text-2xl font-medium ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {getTimeBasedGreeting()}
            </h1>
          ) : (
            <div className="h-8 flex items-center">
              <div className={`animate-pulse h-6 bg-gray-300 rounded w-48 ${
                theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
              }`}></div>
            </div>
          )}
        </div>
        
        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-6 pb-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
